import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { BarChart3, Loader2, RotateCcw } from 'lucide-react'
import { apiRequest } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const usageSchema = z.object({
  state: z.object({
    plan: z.string(),
    monthlyBudgetEur: z.number(),
    rolloverBudgetEur: z.number(),
    topupCreditsEur: z.number(),
    budgetAvailableEur: z.number(),
    llmCostEur: z.number(),
    llmTokensIn: z.number(),
    llmTokensOut: z.number(),
    storageBytes: z.number(),
    // M5-B: Quellen-Aufschluesselung des Speichers — optional, damit aeltere
    // Backends ohne das Feld klarkommen.
    storageBreakdown: z
      .object({
        vault_files: z.number(),
        chat_attachments: z.number(),
      })
      .optional(),
    storageQuotaBytes: z.number(),
  }),
  by_day: z.record(z.number()),
  by_model: z.record(z.object({ cost_eur: z.number(), tokens_in: z.number(), tokens_out: z.number() })),
  by_operation: z.record(z.object({ cost_eur: z.number(), count: z.number() })),
})

export function SettingsUsagePage() {
  const usage = useQuery({
    queryKey: ['billing-usage'],
    queryFn: () => apiRequest({ path: '/billing/usage', schema: usageSchema }),
    refetchInterval: 30_000,
    retry: 1,
  })

  const state = usage.data?.state
  const usageData = usage.data
  const hasError = usage.isError && !usageData
  const isLoading = usage.isLoading && !usageData && !hasError

  const chartDays = usageData ? Object.entries(usageData.by_day) : []
  const modelRows = usageData ? Object.entries(usageData.by_model) : []
  const operationRows = usageData ? Object.entries(usageData.by_operation) : []

  return (
    // R1: ohne doppelte Aussenpadding-Schicht — mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
      <header>
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">Nutzung</h1>
        <p className="mt-1 text-sm text-muted-foreground">KI-Budget, Guthaben, Speicher und Verlauf.</p>
      </header>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Nutzung wird geladen…
          </CardContent>
        </Card>
      ) : hasError ? (
        <Card>
          <CardContent className="space-y-4 py-8">
            <p className="text-sm text-destructive">
              Nutzung konnte nicht geladen werden.
              {usage.error instanceof Error ? ` ${usage.error.message}` : ''}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => usage.refetch()}
              disabled={usage.isFetching}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      ) : !state || !usageData ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Keine Nutzungsdaten verfügbar.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 min-[761px]:grid-cols-3">
            <Metric
              title="KI-Budget"
              value={`${state.llmCostEur.toFixed(2)} / ${money(state.budgetAvailableEur)}`}
              ratio={state.llmCostEur / state.budgetAvailableEur}
            />
            <Metric title="Guthaben" value={money(state.topupCreditsEur)} ratio={0} />
            <Metric
              title="Speicher"
              value={`${bytes(state.storageBytes)} / ${bytes(state.storageQuotaBytes)}`}
              ratio={state.storageBytes / state.storageQuotaBytes}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px] font-[650]">
                <BarChart3 className="h-4 w-4 shrink-0" />
                Letzte 30 Tage
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chartDays.length > 0 ? (
                // R1: beschriftete Region mit horizontalem Scroll auf engen Breiten.
                <div
                  role="region"
                  aria-label="Kostenverlauf der letzten 30 Tage"
                  className="overflow-x-auto"
                >
                  <div className="flex h-44 min-w-[560px] items-end gap-1">
                    {chartDays.map(([day, value]) => (
                      <div key={day} className="flex flex-1 flex-col items-center gap-2">
                        <div
                          className="w-full rounded-t bg-primary/70"
                          style={{ height: `${Math.max(4, value * 60)}px` }}
                          title={`${day}: ${money(value)}`}
                        />
                        <span className="text-[10px] text-muted-foreground">{day.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Noch keine Kosten in den letzten 30 Tagen.</p>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 min-[761px]:grid-cols-2">
            <Breakdown
              title="Nach Modell"
              rows={modelRows.map(([name, row]) => [name, money(row.cost_eur)])}
            />
            <Breakdown
              title="Nach Operation"
              rows={operationRows.map(([name, row]) => [name, `${money(row.cost_eur)} · ${row.count}`])}
            />
            {state.storageBreakdown ? (
              <Breakdown
                title="Speicher nach Quelle"
                rows={[
                  ['Dateien', bytes(state.storageBreakdown.vault_files)],
                  ['Anhänge', bytes(state.storageBreakdown.chat_attachments)],
                ]}
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ title, value, ratio }: { title: string; value: string; ratio: number }) {
  const pct = Number.isFinite(ratio) ? Math.min(100, Math.max(0, ratio * 100)) : 0
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="break-words text-lg font-semibold">{value}</p>
        <div className="mt-3 h-2 rounded bg-muted">
          <div className="h-2 rounded bg-primary" style={{ width: `${pct}%` }} />
        </div>
      </CardContent>
    </Card>
  )
}

function Breakdown({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[16px] font-[650]">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length ? (
          rows.map(([name, value]) => (
            <div key={name} className="flex flex-wrap justify-between gap-x-4 gap-y-1 rounded-md border p-3 text-sm">
              <span className="min-w-0 break-all">{name}</span>
              <span className="font-mono">{value}</span>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">Noch keine Daten.</p>
        )}
      </CardContent>
    </Card>
  )
}

function money(value: number): string {
  if (!Number.isFinite(value)) return 'unlimitiert'
  // M5-A: Backend misst ehrlich in EUR — keine Fake-CHF-Umrechnung.
  return `${value.toFixed(2)} EUR`
}

function bytes(value: number): string {
  if (!Number.isFinite(value)) return 'unlimitiert'
  if (value > 1024 * 1024 * 1024) return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`
  return `${(value / 1024 / 1024).toFixed(0)} MB`
}
